/**
 * Extract plain text from Excel workbooks (.xlsx / .xls) for knowledgebase indexing.
 * Uses the same sheet-to-text approach as Owlbert Electron local directory import.
 */

import * as XLSX from 'xlsx'

export type ExcelExtractResult =
  | { ok: true; text: string }
  | { ok: false; error: 'excel_extract_failed' }

export async function extractExcelText(bytes: Uint8Array | ArrayBuffer): Promise<ExcelExtractResult> {
  try {
    const data =
      bytes instanceof ArrayBuffer
        ? new Uint8Array(bytes)
        : bytes instanceof Uint8Array
          ? bytes
          : new Uint8Array(bytes)
    const workbook = XLSX.read(data, { type: 'array' })
    const parts: string[] = []
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      if (!sheet) continue
      const text = XLSX.utils.sheet_to_txt(sheet, { FS: '\t', RS: '\n' })
      if (text.trim()) parts.push(`Sheet: ${sheetName}\n${text}`)
    }
    const text = parts.join('\n\n').trim()
    return { ok: true, text: text || '(No extractable text)' }
  } catch {
    return { ok: false, error: 'excel_extract_failed' }
  }
}

export type ExcelHtmlResult =
  | { ok: true; html: string }
  | { ok: false; error: 'excel_html_failed' }

/**
 * Render an Excel workbook as one HTML table per sheet for preview.
 * Best-effort: never throws. Used only for display, not for KB indexing.
 */
export async function extractExcelHtml(bytes: Uint8Array | ArrayBuffer): Promise<ExcelHtmlResult> {
  try {
    const data =
      bytes instanceof ArrayBuffer
        ? new Uint8Array(bytes)
        : bytes instanceof Uint8Array
          ? bytes
          : new Uint8Array(bytes)
    const workbook = XLSX.read(data, { type: 'array' })
    const parts: string[] = []
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      if (!sheet) continue
      const table = sheetToSpreadsheetHtml(sheet)
      parts.push(`<h3 class="owlbert-sheet-title">${escapeHtml(sheetName)}</h3>${table}`)
    }
    const html = parts.join('\n').trim()
    return { ok: true, html }
  } catch {
    return { ok: false, error: 'excel_html_failed' }
  }
}

/**
 * Render a single sheet as a spreadsheet-like HTML table:
 * - a header row of column letters (A, B, C…)
 * - a left gutter of row numbers (1, 2, 3…)
 * - merged cells preserved as colspan/rowspan
 */
function sheetToSpreadsheetHtml(sheet: XLSX.WorkSheet): string {
  const ref = sheet['!ref']
  if (!ref) return '<table class="owlbert-xl-table"><tbody></tbody></table>'
  const range = XLSX.utils.decode_range(ref)

  // Map covered (non-master) merged cells -> skip; master cells -> span sizes.
  const covered = new Set<string>()
  const spans = new Map<string, { rs: number; cs: number }>()
  for (const m of sheet['!merges'] ?? []) {
    const masterKey = `${m.s.r}:${m.s.c}`
    spans.set(masterKey, { rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 })
    for (let r = m.s.r; r <= m.e.r; r++) {
      for (let c = m.s.c; c <= m.e.c; c++) {
        if (r === m.s.r && c === m.s.c) continue
        covered.add(`${r}:${c}`)
      }
    }
  }

  // Header row: corner + column letters.
  const headerCells: string[] = ['<th class="owlbert-xl-corner"></th>']
  for (let c = range.s.c; c <= range.e.c; c++) {
    headerCells.push(`<th class="owlbert-xl-colhead">${escapeHtml(XLSX.utils.encode_col(c))}</th>`)
  }
  const thead = `<thead><tr>${headerCells.join('')}</tr></thead>`

  const bodyRows: string[] = []
  for (let r = range.s.r; r <= range.e.r; r++) {
    const cells: string[] = [`<th class="owlbert-xl-rowhead">${r + 1}</th>`]
    for (let c = range.s.c; c <= range.e.c; c++) {
      const key = `${r}:${c}`
      if (covered.has(key)) continue
      const addr = XLSX.utils.encode_cell({ r, c })
      const cell = sheet[addr]
      const raw = cell ? (cell.w ?? (cell.v == null ? '' : String(cell.v))) : ''
      const isNumeric = cell != null && cell.t === 'n'
      const span = spans.get(key)
      const attrs: string[] = []
      if (span && span.cs > 1) attrs.push(`colspan="${span.cs}"`)
      if (span && span.rs > 1) attrs.push(`rowspan="${span.rs}"`)
      if (isNumeric) attrs.push('class="owlbert-xl-num"')
      const attrStr = attrs.length ? ` ${attrs.join(' ')}` : ''
      cells.push(`<td${attrStr}>${escapeHtml(raw)}</td>`)
    }
    bodyRows.push(`<tr>${cells.join('')}</tr>`)
  }

  return `<table class="owlbert-xl-table">${thead}<tbody>${bodyRows.join('')}</tbody></table>`
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
